import React, { useEffect, useState } from "react";
import { Lock } from "lucide-react";
import { onboardingStage, QUIET_PROGRESS_OPTIONS, useOnboardingProgress } from "@/hooks/useOnboardingProgress";
import "./LockedRow.scss";

const DAY_MS = 24 * 60 * 60 * 1000;
export const QUIET_DAYS = 7;

const storageKey = (id) => `lockedRow:${id}:dismissedAt`;
const sessionKey = (id) => `lockedRow:${id}:shown`;

const read = (store, key) => {
  try {
    return store.getItem(key);
  } catch (error) {
    return null;
  }
};

const write = (store, key, value) => {
  try {
    store.setItem(key, value);
  } catch (error) {
    return null;
  }
  return value;
};

export const quietUntil = (dismissedAt) => (dismissedAt ? Number(dismissedAt) + QUIET_DAYS * DAY_MS : 0);

export const lockVisible = ({ stage, dismissible, quietUntilMs, now = Date.now(), firstThisSession }) => {
  if (stage === "new" || stage === "unknown") return false;
  if (!dismissible) return true;
  if (now < quietUntilMs) return false;
  return firstThisSession;
};

const LockedRow = ({ id, title, body, actionLabel, onAction, dismissible = true, requireFirstListing = true }) => {
  const progress = useOnboardingProgress(requireFirstListing, QUIET_PROGRESS_OPTIONS);
  const stage = requireFirstListing ? onboardingStage(progress.data) : "done";
  const [hidden, setHidden] = useState(false);
  const [firstThisSession] = useState(() => !read(window.sessionStorage, sessionKey(id)));
  const [quietUntilMs] = useState(() => quietUntil(read(window.localStorage, storageKey(id))));
  const visible = !hidden && lockVisible({ stage, dismissible, quietUntilMs, firstThisSession });

  useEffect(() => {
    if (visible && dismissible) write(window.sessionStorage, sessionKey(id), "1");
  }, [visible, dismissible, id]);

  if (!visible) return null;

  return (
    <div className="locked-row" role="group" aria-label={title}>
      <span className="locked-row__icon" aria-hidden="true">
        <Lock size={15} strokeWidth={1.75} />
      </span>
      <div className="locked-row__text">
        <b className="locked-row__title">{title}</b>
        <span className="locked-row__body">{body}</span>
      </div>
      <div className="locked-row__actions">
        <button type="button" className="locked-row__action" onClick={onAction}>
          {actionLabel}
        </button>
        {dismissible ? (
          <button
            type="button"
            className="locked-row__dismiss"
            aria-label={`Hide ${title}`}
            onClick={() => {
              write(window.localStorage, storageKey(id), String(Date.now()));
              setHidden(true);
            }}
          >
            <span aria-hidden="true">&times;</span>
          </button>
        ) : null}
      </div>
    </div>
  );
};

export default LockedRow;
